import React, { FC } from 'react';

import styles from './AssetCategoryTable.module.scss';
import DataTable from '../dataTable/DataTable';
import {
  TABLE_HEADING_HOLDINGS_LIST,
  TABLE_HEADING_HOLDINGS
} from '../../utils/constants';

import { standardDateFormat } from '../../utils/dateFormatter';
import { kebabCase } from 'lodash';
import SeeDefinitions from '../../components/styled/SeeDefinitions';
import MSCIStatement from '../../components/styled/MSCIStatement';
import { formatCurrencyChange } from '../../utils/numberFormatter';
import {
  transformAssetTotal,
  transformSector
} from '../../utils/transformSector';

import NumericChange from '../shared/NumericChange';

function numericChange(value: number) {
  return <NumericChange formatFunction={formatCurrencyChange} value={value} />;
}

type Props = {
  assetCategory: AssetCategory;
  asOfDate: string;
  includeDisclaimer: boolean;
};

const AssetCategoryTable: FC<Props> = ({
  assetCategory: { sectors, assetName, assetTotal },
  asOfDate,
  includeDisclaimer
}) => {
  const totalClass = styles['total-asset'];
  const totalTableData = transformAssetTotal(
    assetName,
    assetTotal,
    { rowClass: totalClass },
    numericChange
  );

  const sectorClass = styles[kebabCase(assetName)];
  const sectorTableData = sectors
    .map((sector) =>
      transformSector(
        sector,
        { rowClass: sectorClass },
        numericChange,
        assetName,
        assetTotal.percentOfPortfolio
      )
    )
    .reduce((combined, rowData) => [...combined, ...rowData], []);

  const holdingsTableData = [...totalTableData, ...sectorTableData];

  return (
    <div className={styles['asset-category-table']}>
      <div className={styles['title-section']}>
        <span className={styles['table-title']}>{assetName}</span>
        <span className={styles['table-date']}>
          As of {standardDateFormat(asOfDate)}
        </span>
      </div>
      <div className={styles['table']}>
        <DataTable
          rowData={holdingsTableData}
          headers={TABLE_HEADING_HOLDINGS_LIST}
          tableFormatClass={styles['table-row-structure']}
          labelHeader={TABLE_HEADING_HOLDINGS.NAME}
          valueHeader={TABLE_HEADING_HOLDINGS.MARKET_VALUE}
        />
        {includeDisclaimer && (
          <div className={styles['disclaimer']}>
            <SeeDefinitions
              additionalText="Rounding may cause figures to vary from totals shown."
              className={styles['legal-disclaimer']}
            />
            <MSCIStatement />
            <p>
              The specific securities identified and described above do not
              represent all of the securities purchased, sold or recommended for
              clients in the composite and no assumptions should be made that
              the securities identified and discussed were or will be
              profitable.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetCategoryTable;
